import apiClient from './axios';
import { CSV_UPLOAD_TIMEOUT_MS } from './csv';
import { useDummyDataConfig } from '../config/dummyDataConfig';
import { addonItemsFor } from '../config/addonItems';
import {
  influencersListDummyData,
  influencerDetailDummyData,
  createInfluencerDummyData,
  updateInfluencerDummyData,
  deleteInfluencerDummyData,
  csvUploadDummyData,
  bulkUpdateStatusDummyData,
  influencerReviewsDummyData,
  updateFollowersDummyData,
  updateContactDummyData,
  updateKolSupplyStepDummyData,
  updateClientTotalFeeDummyData,
  getInfluencerShippingAddressDummyData
} from '../data/influencersDummyData';

/**
 * KOL 을 등록할 때 깔아 둘 부가 항목 줄.
 * 항목 이름과 기준 단가를 「부가 항목 기준」에서 가져온다 — 코드에 박아 두지 않는다.
 * 기준을 아직 안 채웠으면 단가는 0 이다(예전과 같은 상태).
 */
const seedAddonRows = (currency) =>
  addonItemsFor(currency).map((a) => ({
    item: a.name,
    unavailable: false,
    monthsUnits: 1,
    rate: a.rate ?? 0,
    free: false,
  }));


/**
 * 인플루언서 목록 조회 (Flattened 구조)
 * 플랫폼-콘텐츠타입 조합별로 펼쳐진 데이터를 반환
 */
export const getInfluencers = async (params = {}) => {
  // 더미 데이터 사용 여부 확인
  if (useDummyDataConfig.influencerList) {
    return new Promise((resolve) => {
      setTimeout(() => {
        // 더미 데이터 필터링
        let filteredData = [...influencersListDummyData.data];

        // 각 인플루언서의 avgRate와 reviewCount를 리뷰 데이터 기반으로 계산
        filteredData = filteredData.map(influencer => {
          const influencerReviews = influencerReviewsDummyData.data.reviews.filter(
            review => review.influencerId === influencer.id
          );

          const reviewCount = influencerReviews.length;
          let avgRate = 0;

          if (reviewCount > 0) {
            const totalRating = influencerReviews.reduce((sum, review) => {
              return sum + (review.satisfactionEvaluation?.overall || review.rating || 0);
            }, 0);
            avgRate = totalRating / reviewCount;
          }

          return {
            ...influencer,
            avgRate: avgRate,
            reviewCount: reviewCount
          };
        });

        // 검색어 필터링 (kolId, representativeName 등)
        if (params.search) {
          const searchLower = params.search.toLowerCase();
          filteredData = filteredData.filter(inf => {
            const kolId = (inf.kolId || '').toLowerCase();
            const representativeName = (inf.representativeName || '').toLowerCase();
            const fullName = (inf.fullName || '').toLowerCase();
            return kolId.includes(searchLower) ||
                   representativeName.includes(searchLower) ||
                   fullName.includes(searchLower);
          });
        }

        // KOL ID 검색
        if (params.kolId) {
          const kolIdLower = params.kolId.toLowerCase();
          filteredData = filteredData.filter(inf => {
            const kolId = (inf.kolId || '').toLowerCase();
            return kolId.includes(kolIdLower);
          });
        }

        // 닉네임 검색
        if (params.nickname) {
          const nicknameLower = params.nickname.toLowerCase();
          filteredData = filteredData.filter(inf => {
            const representativeName = (inf.representativeName || '').toLowerCase();
            const fullName = (inf.fullName || '').toLowerCase();
            return representativeName.includes(nicknameLower) ||
                   fullName.includes(nicknameLower);
          });
        }

        // 플랫폼 타입 필터링
        if (params.platformType) {
          filteredData = filteredData.filter(inf => {
            const mainPlatform = inf.platforms?.[0]?.platformType || '';
            return mainPlatform.toLowerCase() === params.platformType.toLowerCase();
          });
        }

        // 국가 필터링 (ISO 코드 사용)
        if (params.country) {
          const countries = params.country.split(',');
          filteredData = filteredData.filter(inf => {
            const infCountry = inf.country || '';
            // ISO 코드와 비교
            return countries.some(c => {
              const isoCode = c.toUpperCase();
              // 직접 ISO 코드 비교
              if (infCountry.toUpperCase() === isoCode) return true;
              // 레거시 풀네임도 지원
              const countryNameMap = {
                'VN': 'VIETNAM',
                'TH': 'THAILAND',
                'KR': 'KOREA',
                'US': 'US',
                'JP': 'JAPAN',
                'TW': 'TAIWAN',
                'HK': 'HONGKONG',
                'CN': 'CHINA',
                'SG': 'SINGAPORE',
                'ID': 'INDONESIA',
                'MY': 'MALAYSIA',
                'PH': 'PHILIPPINE'
              };
              const fullName = countryNameMap[isoCode];
              return fullName && infCountry.toUpperCase().includes(fullName);
            });
          });
        }

        // 팔로워 수 필터링
        if (params.minFollowers != null || params.maxFollowers != null) {
          filteredData = filteredData.filter(inf => {
            const followers = inf.followers || 0;
            const min = params.minFollowers != null ? params.minFollowers : 0;
            const max = params.maxFollowers != null ? params.maxFollowers : Infinity;
            return followers >= min && followers <= max;
          });
        }

        // KOL 가격 필터링
        if (params.kolMinPrice != null || params.kolMaxPrice != null || params.kolCurrency) {
          filteredData = filteredData.filter(inf => {
            const kolSupply = inf.kolSupplyStep;
            if (!kolSupply) return false;

            if (params.kolCurrency && kolSupply.currency !== params.kolCurrency) {
              return false;
            }

            const amount = kolSupply.amount || 0;
            const min = params.kolMinPrice != null ? params.kolMinPrice : 0;
            const max = params.kolMaxPrice != null ? params.kolMaxPrice : Infinity;
            return amount >= min && amount <= max;
          });
        }

        // Client 가격 필터링
        if (params.clientMinPrice != null || params.clientMaxPrice != null || params.clientCurrency) {
          filteredData = filteredData.filter(inf => {
            const clientFee = inf.clientTotalFee;
            if (!clientFee) return false;

            if (params.clientCurrency && clientFee.currency !== params.clientCurrency) {
              return false;
            }

            const amount = clientFee.amount || 0;
            const min = params.clientMinPrice != null ? params.clientMinPrice : 0;
            const max = params.clientMaxPrice != null ? params.clientMaxPrice : Infinity;
            return amount >= min && amount <= max;
          });
        }

        // 운영중 필터링
        if (params.contentOn === true) {
          filteredData = filteredData.filter(inf => inf.contentOn === true);
        }

        // 정렬
        if (params.sortBy && params.sortOrder) {
          filteredData.sort((a, b) => {
            let aValue, bValue;

            if (params.sortBy === 'createdAt') {
              aValue = new Date(a.createdAt || 0).getTime();
              bValue = new Date(b.createdAt || 0).getTime();
            } else if (params.sortBy === 'expectedPrice') {
              // Client Total Fee 기준으로 정렬
              aValue = a.clientTotalFee?.amount || 0;
              bValue = b.clientTotalFee?.amount || 0;
            } else {
              return 0;
            }

            if (params.sortOrder === 'asc') {
              return aValue - bValue;
            } else {
              return bValue - aValue;
            }
          });
        }

        resolve({
          success: true,
          data: filteredData,
          meta: {
            total: filteredData.length,
            totalPages: Math.ceil(filteredData.length / (params.limit || 20)),
            currentPage: params.page || 1,
            limit: params.limit || 20
          }
        });
      }, 300);
    });
  }

  // 실제 API 호출
  const response = await apiClient.get('/api/influencers', {
    params: {
      page: params.page || 1,
      limit: params.limit || 20,
      ...(params.sortBy && { sortBy: params.sortBy }),
      ...(params.sortOrder && { sortOrder: params.sortOrder }),
      ...(params.search && { search: params.search }),
      ...(params.id && { id: params.id }),
      ...(params.kolId && { kolId: params.kolId }),
      ...(params.nickname && { nickname: params.nickname }),
      ...(params.contentOn != null && { contentOn: params.contentOn }),
      ...(params.country && { country: params.country }),
      ...(params.platformType && { platformType: params.platformType }),
      ...(params.contentType && { contentType: params.contentType }),
      ...(params.gender && { gender: params.gender }),
      ...(params.grade && { grade: params.grade }),
      ...(params.minFollowers != null && { minFollowers: params.minFollowers }),
      ...(params.maxFollowers != null && { maxFollowers: params.maxFollowers }),
      ...(params.minViews != null && { minViews: params.minViews }),
      ...(params.maxViews != null && { maxViews: params.maxViews }),
      ...(params.minLikes != null && { minLikes: params.minLikes }),
      ...(params.maxLikes != null && { maxLikes: params.maxLikes }),
      ...(params.kolCurrency && { kolCurrency: params.kolCurrency }),
      ...(params.kolMinPrice != null && { kolMinPrice: params.kolMinPrice }),
      ...(params.kolMaxPrice != null && { kolMaxPrice: params.kolMaxPrice }),
      ...(params.clientCurrency && { clientCurrency: params.clientCurrency }),
      ...(params.clientMinPrice != null && { clientMinPrice: params.clientMinPrice }),
      ...(params.clientMaxPrice != null && { clientMaxPrice: params.clientMaxPrice }),
      ...(params.isVerified != null && { isVerified: params.isVerified }),
      ...(params.include && { include: params.include }),
    },
  });
  // 서비스는 { data, meta } 형태를 반환함
  return response.data;
};

/**
 * 인플루언서 상세 조회
 */
export const getInfluencerById = async (id) => {
  // 더미 데이터 사용 여부 확인
  if (useDummyDataConfig.influencerDetail) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(influencerDetailDummyData);
      }, 300);
    });
  }

  // 실제 API 호출
  const response = await apiClient.get(`/api/influencers/${id}`);
  return response.data;
};

/**
 * 인플루언서 생성
 * @param {Object} data - 인플루언서 데이터
 * @param {string} data.representativeNickname - 대표 닉네임
 * @param {string} data.country - 국가 코드
 * @param {Array} data.platforms - 플랫폼 정보 배열
 * @param {Array} data.contacts - 연락처 정보 배열
 */
export const createInfluencer = async (data) => {
  // 더미 데이터 사용 여부 확인
  if (useDummyDataConfig.createInfluencer) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(createInfluencerDummyData);
      }, 500);
    });
  }

  // 실제 API 호출
  const response = await apiClient.post('/api/influencers', data);
  return response.data;
};

/**
 * 인플루언서 생성 (모달에서 사용)
 * InfluencerRegisterModal의 데이터를 백엔드 API 형식으로 변환하여 생성
 * @param {Object} modalData - 모달 데이터
 * @param {Array} exchangeRates - 환율 설정 배열 (optional)
 */
// 국가 라벨("Vietnam" 등) → 국가 코드
const COUNTRY_CODE_MAP = {
  'Vietnam': 'VN',
  'Thailand': 'TH',
  'Korea': 'KR',
  'US': 'US',
  'Japan': 'JP',
  'Taiwan': 'TW',
  'HongKong': 'HK',
  'China': 'CN',
  'Singapore': 'SG',
  'Indonesia': 'ID',
  'Malaysia': 'MY',
  'Philippine': 'PH'
};

// 국가 코드 → 기본 통화
const DEFAULT_CURRENCY_BY_COUNTRY_CODE = {
  'VN': 'VND',
  'TH': 'THB',
  'KR': 'KRW',
  'SG': 'SGD',
  'MY': 'MYR',
  'ID': 'IDR',
  'PH': 'PHP',
  'JP': 'JPY',
  'TW': 'TWD',
  'HK': 'HKD',
  'CN': 'CNY',
  'US': 'USD'
};

/** 국가 라벨("Vietnam" 등)로 기본 통화 코드를 구한다. createInfluencerFromModal과
 *  등록 후 통화 보정(CreatorCollectionContext)이 같은 매핑을 쓰도록 공유. */
/* 2026-10-12 개발사 흔적 점검 H2: 국가를 못 찾으면 개발사 코드가 'VN'·VND 로 정했다.
   등록 창 라벨이 조금만 달라도 태국 KOL 이 베트남으로 등록됐고, 서버 줄의 국가 코드('TH')를 넘기는
   캠페인 빠른 배정(CampaignInfluencerTab)도 늘 VND 가 됐다. 라벨과 코드 둘 다 받고, 모르면 null 을 돌려준다. */
export const countryCodeOf = (country) => {
  if (!country) return null;
  if (COUNTRY_CODE_MAP[country]) return COUNTRY_CODE_MAP[country];
  const upper = String(country).trim().toUpperCase();
  if (DEFAULT_CURRENCY_BY_COUNTRY_CODE[upper]) return upper;
  const byLabel = Object.keys(COUNTRY_CODE_MAP).find((label) => label.toUpperCase() === upper);
  return byLabel ? COUNTRY_CODE_MAP[byLabel] : null;
};

/** 모르는 국가면 null — 부르는 쪽이 다른 값으로 대신하거나 멈춘다(예전처럼 VND 로 정하지 않는다) */
export const getDefaultCurrencyForCountry = (countryLabel) => {
  const code = countryCodeOf(countryLabel);
  return code ? DEFAULT_CURRENCY_BY_COUNTRY_CODE[code] || null : null;
};

export const createInfluencerFromModal = async (modalData, exchangeRates = []) => {
  // 통화에 해당하는 세금 타입 가져오기
  const getTaxTypeForCurrency = (currency) => {
    const rateSetting = exchangeRates.find(r => r.from === currency);
    if (rateSetting?.tax) {
      const taxLabel = rateSetting.tax;
      if (taxLabel === 'No Tax' || taxLabel === 'no_tax') return 'no_tax';
      const match = taxLabel.match(/Tax\s*(\d+)%?/i);
      if (match) return `tax_${match[1]}`;
      return taxLabel.toLowerCase().replace(/\s+/g, '_');
    }
    return 'no_tax';
  };

  // platform은 이미 올바른 ENUM 형식 ("Instagram", "Facebook", "Youtube" 등)
  // 변환 불필요 - UI label이 곧 백엔드 ENUM 값

  // channel은 이미 올바른 ENUM 형식 ("Line", "Zalo", "Kakao", "TT", "IG", "Whatsapp", "phone", "email", "Others")
  // 변환 불필요 - UI label이 곧 백엔드 ENUM 값

  // 국가를 모르면 VN 으로 정하지 않고 등록을 멈춘다(2026-10-12 점검 H2) — 부르는 화면이 이 문구를 그대로 띄운다
  const country = countryCodeOf(modalData.country);
  const blbCurrency = getDefaultCurrencyForCountry(modalData.country);
  if (!country || !blbCurrency) {
    throw new Error(`국가 «${modalData.country || '(비어 있음)'}»를 알 수 없어 등록하지 않았습니다. 국가를 다시 골라 주세요.`);
  }

  // 디버깅: 국가 및 통화 확인
  console.log('[createInfluencerFromModal] modalData.country:', modalData.country);
  console.log('[createInfluencerFromModal] country (mapped):', country);
  console.log('[createInfluencerFromModal] blbCurrency:', blbCurrency);

  // contentType은 이미 올바른 ENUM 형식 ("IG Pics", "IG Reels" 등)
  // 변환 불필요 - UI label이 곧 백엔드 ENUM 값

  // 플랫폼 데이터 변환
  const platforms = modalData.channels.map(channel => ({
    platformType: channel.platform, // platform은 이미 올바른 ENUM 형식
    profileId: channel.profileUrl?.replace(/^.*\.com\//, '') || '',  // URL에서 ID 추출
    profileUrl: channel.profileUrl || '',
    followers: parseInt(channel.followers?.replace(/,/g, '') || '0', 10),
    views: parseInt(channel.views?.replace(/,/g, '') || '0', 10),  // 조회수
    likes: parseInt(channel.likes?.replace(/,/g, '') || '0', 10),  // 좋아요수
    contentTypes: channel.contentFormats || [],  // 활동 콘텐츠 포맷
    adminNote: channel.memo || null,
    blbCurrency: blbCurrency,  // 국가별 기본 통화
    kolBaseCurrency: blbCurrency,  // KOL 기본단가 통화 (국가 기반)
    clientBaseCurrency: 'KRW'  // 클라이언트 통화
  }));

  // contents 배열 생성 (플랫폼별 contentType별로)
  const contents = [];
  modalData.channels.forEach(channel => {
    const platformType = channel.platform; // platform은 이미 올바른 ENUM 형식
    const contentFormats = channel.contentFormats || [];

    contentFormats.forEach(contentTypeLabel => {
      // contentType은 이미 올바른 ENUM 형식이므로 변환 불필요
      const contentType = contentTypeLabel;

      // 각 contentType에 대한 기본 구조 생성
      contents.push({
        platformType: platformType,
        contentType: contentType,
        // 가격 정보 초기값
        kolBaseCurrency: blbCurrency,
        kolBaseTotalPrice: 0,
        kolBaseTaxType: getTaxTypeForCurrency(blbCurrency),
        clientBaseCurrency: 'KRW',
        clientBasePrice: 0,
        blbExpectedRevenue: 0,
        localTaxRemittanceRequired: false,
        contentOn: false,
        /* 부가 항목 — 항목 이름과 기준 단가를 「부가 항목 기준」에서 가져온다.
           예전에는 이 자리에 항목 세 개가 문자열로 박혀 있었고 단가는 늘 0 이라,
           담당자가 KOL 마다 손으로 쳤다 — 같은 항목에 사람마다 다른 금액이 들어갔다.
           (2026-07-31 대표님: 「이 값은 어디서 설정하는 거야」 → 설정할 자리가 없었다)
           기준 단가를 아직 안 채웠으면 예전처럼 0 으로 시작한다. */
        additionalFees: seedAddonRows(blbCurrency),
        packagePrices: [],
        subcontents: [],
        others: [
          {
            type: 'Tiktok Affiliate',
            details: 'Not Available'
          },
          {
            type: 'Shopee Affiliate',
            details: 'Not Available'
          }
        ]
      });
    });
  });

  // 유효한 연락처만 필터링
  const validContacts = modalData.contacts
    .filter(contact =>
      contact.channel !== '컨택 채널' &&
      contact.idOrEmail &&
      contact.idOrEmail.trim() !== ''
    )
    .map(contact => ({
      channel: contact.channel, // channel은 이미 올바른 ENUM 형식
      contactId: contact.idOrEmail
    }));

  // 백엔드 API 형식에 맞게 데이터 구성
  const influencerData = {
    representativeNickname: modalData.representativeName,
    country: country,
    platforms: platforms.length > 0 ? platforms : undefined,
    // contents 배열 (고정 항목 포함)
    ...(contents.length > 0 && { contents: contents }),
    // 컨택 수단 (있는 경우에만 포함)
    ...(validContacts.length > 0 && { contacts: validContacts }),
    // 추가 정보 (있는 경우에만 포함)
    ...(modalData.lastName && { lastName: modalData.lastName }),
    ...(modalData.firstName && { firstName: modalData.firstName }),
    ...(modalData.fullName && { fullName: modalData.fullName }),
    ...(modalData.idNo && { nationalId: modalData.idNo }),
    ...(modalData.phone && { shippingPhone: modalData.phone }),
    ...(modalData.deliveryRecipientName && { shippingName: modalData.deliveryRecipientName }),
    ...(modalData.deliveryAddress && { shippingAddress: modalData.deliveryAddress }),
    ...(modalData.deliveryPostalCode && { shippingPostcode: modalData.deliveryPostalCode }),
    ...(modalData.bankName && { bankName: modalData.bankName }),
    ...(modalData.accountNumber && { bankAccountNumber: modalData.accountNumber }),
    ...(modalData.bankAddress && { bankAddress: modalData.bankAddress })
  };

  try {
    // 디버깅: 전송될 데이터 확인
    console.log('[createInfluencerFromModal] contents[0]?.kolBaseCurrency:', contents[0]?.kolBaseCurrency);
    console.log('[createInfluencerFromModal] influencerData:', JSON.stringify(influencerData, null, 2));

    // 인플루언서 생성 (contacts 포함)
    const influencer = await createInfluencer(influencerData);

    return influencer;
  } catch (error) {
    console.error('Failed to create influencer:', error);
    throw error;
  }
};

/**
 * 인플루언서 수정
 */
export const updateInfluencer = async (id, data) => {
  // 더미 데이터 사용 여부 확인
  if (useDummyDataConfig.updateInfluencer) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(updateInfluencerDummyData);
      }, 500);
    });
  }

  // 실제 API 호출
  const response = await apiClient.patch(`/api/influencers/${id}`, data);
  return response.data;
};

/**
 * 인플루언서 콘텐츠 타입별 가격 정보 수정
 * @param {string} influencerId - 인플루언서 ID
 * @param {Object} pricingData - 가격 정보
 * @param {string} pricingData.platformId - 플랫폼 ID
 * @param {string} pricingData.contentType - 콘텐츠 타입 (예: IG Reels, FB)
 * @param {string} pricingData.baseCurrency - 기본단가 통화
 * @param {number} pricingData.kolBaseRate - KOL 기본 단가
 * @param {string} pricingData.clientCurrency - 클라이언트 공급가 통화
 * @param {number} pricingData.clientSupplyPrice - 클라이언트 공급가
 * @param {string} pricingData.taxType - 세금 유형
 * @param {number} pricingData.companyProfit - 회사 수익
 * @param {number} pricingData.kolFinalRate - KOL 최종 단가
 * @param {boolean} pricingData.localTaxRemittanceRequired - 현지 세금 송금 필요 여부
 */
export const updateInfluencerPricing = async (influencerId, pricingData) => {
  // 더미 데이터 사용 여부 확인
  if (useDummyDataConfig.updateInfluencer) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          success: true,
          message: '가격 정보가 업데이트되었습니다.',
          data: pricingData
        });
      }, 300);
    });
  }

  // 실제 API 호출
  const response = await apiClient.post(`/api/influencers/${influencerId}/pricings`, pricingData);
  return response.data;
};

/**
 * 인플루언서 삭제
 */
export const deleteInfluencer = async (id) => {
  // 더미 데이터 사용 여부 확인
  if (useDummyDataConfig.deleteInfluencer) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(deleteInfluencerDummyData);
      }, 300);
    });
  }

  // 실제 API 호출
  const response = await apiClient.delete(`/api/influencers/${id}`);
  return response.data;
};

/* ===========================
   추가된 엔드포인트들
   =========================== */

/**
 * (플랫폼) Followers 숫자만 수정
 * PATCH /influencers/platforms/:platformId/followers
 */
export const patchPlatformFollowers = async (platformId, followers) => {
  // 더미 데이터 사용 여부 확인
  if (useDummyDataConfig.updateFollowers) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(updateFollowersDummyData);
      }, 300);
    });
  }

  // 실제 API 호출
  const response = await apiClient.patch(
    `/api/influencers/platforms/${platformId}/followers`,
    { followers }
  );
  return response.data;
};

/**
 * 인플루언서 연락처 리스트
 * GET /influencers/:id/contacts
 */
export const getInfluencerContacts = async (influencerId) => {
  // 더미 데이터 사용 여부 확인
  if (useDummyDataConfig.influencerDetail) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          success: true,
          data: influencerDetailDummyData.data.contacts || []
        });
      }, 300);
    });
  }

  // 실제 API 호출
  const response = await apiClient.get(`/api/influencers/${influencerId}/contacts`);
  return response.data;
};

/**
 * 인플루언서 연락처 추가
 * POST /influencers/:id/contacts
 * channel: EMAIL | LINE | WHATSAPP | TELEGRAM | KAKAO | INSTAGRAM_DM | PHONE
 * contactId: string
 */
export const addInfluencerContact = async (influencerId, { channel, contactId, type, value }) => {
  // 더미 데이터 사용 여부 확인
  if (useDummyDataConfig.updateContact) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(updateContactDummyData);
      }, 300);
    });
  }

  // 실제 API 호출
  // type/value를 alias로 받아도 서버에서 처리되도록 그대로 전달
  const payload = {
    ...(channel && { channel }),
    ...(contactId && { contactId }),
    ...(type && { type }),
    ...(value && { value }),
  };
  const response = await apiClient.post(`/api/influencers/${influencerId}/contacts`, payload);
  return response.data;
};

/**
 * 인플루언서 연락처 수정
 * PATCH /api/influencers/:influencerId/contacts/:contactId
 */
export const patchInfluencerContact = async (influencerId, contactId, { channel, contactId: contactValue }) => {
  // 더미 데이터 사용 여부 확인
  if (useDummyDataConfig.updateContact) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(updateContactDummyData);
      }, 300);
    });
  }

  // 실제 API 호출
  const payload = {
    ...(channel && { channel }),
    ...(contactValue && { contactId: contactValue }),
  };
  const response = await apiClient.patch(`/api/influencers/${influencerId}/contacts/${contactId}`, payload);
  return response.data;
};

/**
 * 인플루언서 연락처 삭제
 * DELETE /influencers/contacts/:contactId
 */
export const deleteInfluencerContact = async (contactId) => {
  // 더미 데이터 사용 여부 확인
  if (useDummyDataConfig.updateContact) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          success: true,
          message: '연락처가 성공적으로 삭제되었습니다.'
        });
      }, 300);
    });
  }

  // 실제 API 호출
  const response = await apiClient.delete(`/api/influencers/contacts/${contactId}`);
  return response.data;
};

/**
 * KOL Supply Step 수정 (콘텐츠 단위)
 * PATCH /influencers/contents/:contentId/kol-supply-step
 * { currency, amount, currentAmount?, clientSupplyPrice? }
 */
export const patchKolSupplyStep = async (contentId, { currency, amount, currentAmount, clientSupplyPrice }, session) => {
  // 더미 데이터 사용 여부 확인
  if (useDummyDataConfig.updateKolSupplyStep) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(updateKolSupplyStepDummyData);
      }, 300);
    });
  }

  // 실제 API 호출 — session: 저장 단추에서 이미 받은 핵심 값 확인(askCriticalConfirm), 있으면 확인 창을 또 띄우지 않는다(2026-10-23 확인창 개편)
  const body = {
    currency,
    amount,
    ...(currentAmount != null && { currentAmount }),
    ...(clientSupplyPrice != null && { clientSupplyPrice })
  };
  const url = `/api/influencers/contents/${contentId}/kol-supply-step`;
  const response = session
    ? await apiClient.patch(url, body, { criticalSession: session })
    : await apiClient.patch(url, body);
  return response.data;
};

/**
 * Client Total Fee 수정
 * PATCH /influencers/:influencerId/client-total-fee
 * { currency, amount }
 */
export const patchClientTotalFee = async (influencerId, { currency, amount }) => {
  // 더미 데이터 사용 여부 확인
  if (useDummyDataConfig.updateClientTotalFee) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(updateClientTotalFeeDummyData);
      }, 300);
    });
  }

  // 실제 API 호출
  const response = await apiClient.patch(
    `/api/influencers/${influencerId}/client-total-fee`,
    { currency, amount }
  );
  return response.data;
};

/**
 * Content 운영 상태 변경
 * PATCH /api/influencers/contents/{contentId}
 */
export const patchContentStatus = async (contentId, contentOn) => {
  // 더미 데이터 사용 여부 확인
  if (useDummyDataConfig.bulkUpdateStatus) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({ success: true, data: { id: contentId, contentOn } });
      }, 300);
    });
  }

  // 실제 API 호출
  const response = await apiClient.patch(
    `/api/influencers/contents/${contentId}`,
    { contentOn }
  );
  return response.data;
};

/**
 * Content 필드 업데이트 (일반)
 * PATCH /api/influencers/contents/{contentId}
 * @param {string} contentId - Content ID
 * @param {object} data - 업데이트할 필드들 (clientBasePrice, clientBaseCurrency 등)
 */
export const patchContent = async (contentId, data) => {
  const response = await apiClient.patch(
    `/api/influencers/contents/${contentId}`,
    data
  );
  return response.data;
};

/**
 * 인플루언서 운영 상태 일괄 변경
 * PATCH /api/influencers/bulk-update-status
 * { influencerIds: string[], contentOn: boolean }
 */
export const bulkUpdateInfluencerStatus = async (influencerIds, contentOn) => {
  // 더미 데이터 사용 여부 확인
  if (useDummyDataConfig.bulkUpdateStatus) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(bulkUpdateStatusDummyData);
      }, 300);
    });
  }

  // 실제 API 호출
  const response = await apiClient.patch(
    `/api/influencers/bulk-update-status`,
    { influencerIds, contentOn }
  );
  return response.data;
};

/**
 * CSV 파일 업로드
 * POST /api/influencers/csv-upload
 * @param {File} file - CSV 파일
 */
export const uploadInfluencersCSV = async (file) => {
  // 더미 데이터 사용 여부 확인
  if (useDummyDataConfig.csvUpload) {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        // CSV 파일 검증 시뮬레이션
        if (!file || !file.name.endsWith('.csv')) {
          reject(new Error('CSV 파일만 업로드 가능합니다.'));
          return;
        }

        resolve(csvUploadDummyData);
      }, 1000); // 업로드 시뮬레이션을 위해 1초 대기
    });
  }

  // 실제 API 호출
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiClient.post('/api/influencers/csv-upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    // KOL CSV 도 기본 10초로는 모자라다(2026-10-12 점검 D3)
    timeout: CSV_UPLOAD_TIMEOUT_MS,
  });
  return response.data;
};

/**
 * 인플루언서 리뷰 목록 조회
 * GET /api/influencers/:id/reviews
 * @param {string} influencerId - 인플루언서 ID
 */
export const getInfluencerReviews = async (influencerId) => {
  // 더미 데이터 사용 여부 확인
  if (useDummyDataConfig.influencerReviews) {
    return new Promise((resolve) => {
      setTimeout(() => {
        // 특정 인플루언서의 리뷰만 필터링
        const allReviews = influencerReviewsDummyData.data.reviews;
        const filteredReviews = allReviews.filter(
          review => review.influencerId === influencerId
        );

        resolve({
          success: true,
          data: {
            reviews: filteredReviews,
            pagination: {
              total: filteredReviews.length,
              totalPages: 1,
              currentPage: 1,
              limit: 10
            }
          }
        });
      }, 300);
    });
  }

  // 실제 API 호출
  const response = await apiClient.get(`/api/influencers/${influencerId}/reviews`);
  return response.data;
};

/**
 * 인플루언서 기본 배송지 주소 조회
 * @param {string} influencerId - 인플루언서 ID
 * @returns {Promise} API 응답
 */
export const getInfluencerShippingAddress = async (influencerId) => {
  if (useDummyDataConfig.influencerDetail) {
    return new Promise((resolve) => {
      setTimeout(() => {
        const dummyData = getInfluencerShippingAddressDummyData[influencerId] || {
          success: true,
          data: {
            recipientName: '',
            phone: '',
            address: '',
            postalCode: ''
          }
        };
        resolve(dummyData);
      }, 300);
    });
  }

  const response = await apiClient.get(`/api/influencers/${influencerId}/shipping-address`);
  return response.data;
};

/**
 * 인플루언서 기본 배송지 주소 업데이트
 * @param {string} influencerId - 인플루언서 ID
 * @param {object} addressData - 배송지 주소 데이터
 * @param {string} addressData.recipientName - 수취인 이름
 * @param {string} addressData.phone - 전화번호
 * @param {string} addressData.address - 주소
 * @param {string} addressData.postalCode - 우편번호
 * @returns {Promise} API 응답
 */
export const updateInfluencerShippingAddress = async (influencerId, addressData) => {
  if (useDummyDataConfig.influencerDetail) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          success: true,
          message: '배송지 주소가 업데이트되었습니다.',
          data: {
            influencerId,
            ...addressData
          }
        });
      }, 300);
    });
  }

  const response = await apiClient.put(
    `/api/influencers/${influencerId}/shipping-address`,
    addressData
  );
  return response.data;
};

/**
 * 인플루언서 콘텐츠 세부 조건 조회
 * GET /api/influencers/{id}/platforms/{platformId}/content-types/{contentType}/details
 */
export const getContentDetails = async (influencerId, platformId, contentType) => {
  const response = await apiClient.get(
    `/api/influencers/${influencerId}/platforms/${platformId}/content-types/${contentType}/details`
  );
  return response.data;
};

/**
 * 인플루언서 콘텐츠 세부 조건 수정
 * PATCH /api/influencers/{id}/platforms/{platformId}/content-types/{contentType}/details
 */
export const updateContentDetails = async (influencerId, platformId, contentType, detailsData) => {
  const response = await apiClient.patch(
    `/api/influencers/${influencerId}/platforms/${platformId}/content-types/${contentType}/details`,
    detailsData
  );
  return response.data;
};
